/**
 * Compresses an image file using Canvas.
 * @param file The original image file.
 * @param maxWidth Max width of the resulting image.
 * @param maxHeight Max height of the resulting image.
 * @param quality Quality from 0 to 1.
 * @returns A promise resolving to a compressed Blob.
 */
export async function compressImage(file: File, maxWidth = 1280, maxHeight = 1280, quality = 0.7): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context not available'));

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
          },
          'image/jpeg',
          quality,
        );
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
}

/**
 * Prepares a phone/gallery image for transport without needlessly re-encoding
 * an already-small camera capture. The backend remains the authoritative
 * compressor and validates the source again.
 */
export async function prepareImageUpload(file: File): Promise<File> {
  const supported = /^image\/(jpeg|png|webp)$/i.test(file.type);
  if (supported && file.size <= 1024 * 1024) return file;

  let compressed: Blob;
  try {
    // Some phone pickers omit the MIME type or return HEIC/AVIF. Normalize any
    // image the browser can decode instead of sending an unsupported MIME.
    compressed = await compressImage(file);
  } catch {
    throw new Error('Не удалось прочитать снимок. Выберите фото JPEG/PNG или снимите его системной камерой.');
  }
  const stem = file.name.replace(/\.[^.]+$/, '') || `photo_${Date.now()}`;
  return new File([compressed], `${stem}.jpg`, {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}
